/**
 * Auftrag, Montage, Dokumentation (Kapitel M).
 *
 * Der Teil nach dem Zuschlag. Im Gespraech beschrieben als: Materialliste geht
 * an den Monteur, der macht nach Fertigstellung die Fotos, und am Ende steht
 * die Dokumentation, ohne dass jemand den Pruefbefund von Hand ausfuellt.
 *
 * Die Monteursicht ist keine zweite Anwendung, sondern dieselbe Oberflaeche mit
 * anderer Sicht (PV-M02): Belegung, Stringplan und die detaillierte
 * Materialliste — und sonst nichts.
 */

import { useRef, useState } from 'react';

import type { Pruefvorlage } from '@pv/geometrie';

import { db, neueId, speichere } from '../daten/db.ts';
import { hakeAbFallsOffen, legeDokumentAb } from '../daten/schritte.ts';
import { tagVon } from '../datum.ts';
import type { Anlagendaten } from '../anlage.ts';
import type {
  Angebotszeile,
  Projekt,
  Auftragszeile,
  Checklistenvorlage,
  Pruefbefundzeile,
  Stringzeile,
} from '../typen.ts';
import { Pruefbefundpanel } from './Pruefbefundpanel.tsx';

interface Eigenschaften {
  projektId: string;
  anlage: Anlagendaten;
  strings: Stringzeile[];
  angebote: Angebotszeile[];
  auftraege: Auftragszeile[];
  pruefbefunde: Pruefbefundzeile[];
  vorlagen: Checklistenvorlage[];
  /** Prüfpunkte aus den Einstellungen (`pruefbefund.vorlage`). */
  pruefvorlage: Pruefvorlage;
  onStringplan: () => void;
  onDokumentation: () => void;
  onAnlagenblatt: () => void;
  projekt: Projekt;
  /** Montage: Netzanschluss und Inbetriebnahme stehen am Projekt — das ändern Vertrieb und Büro. */
  nurLesen?: boolean;
}

/**
 * Aufträge vor 0.14 haben ihre Checkliste aus Vorlagen ohne Umlaute kopiert
 * („Material vollstaendig geliefert“). Die Vorlagen sind seit Migration 0026
 * richtig; die gespeicherten Listen bleiben unangetastet — geändert wird nur
 * die Anzeige, sonst bekäme jedes Gerät mit offenen Änderungen einen
 * Konflikt. Nur die Wörter aus den alten Vorlagen, nichts Selbstgeschriebenes.
 */
const ALTE_VORLAGENWOERTER: Array<[RegExp, string]> = [
  [/vollstaendig/g, 'vollständig'],
  [/Geruest/g, 'Gerüst'],
  [/Ueberspannungsschutz/g, 'Überspannungsschutz'],
  [/Zaehlerkasten/g, 'Zählerkasten'],
  [/bestueckt/g, 'bestückt'],
  [/Pruefprotokoll/g, 'Prüfprotokoll'],
  [/geprueft/g, 'geprüft'],
  [/Uebergabe/g, 'Übergabe'],
];

export const mitUmlauten = (text: string): string =>
  ALTE_VORLAGENWOERTER.reduce((t, [muster, ersatz]) => t.replace(muster, ersatz), text);

export function Abwicklungspanel(e: Eigenschaften): React.JSX.Element {
  const auftrag = e.auftraege.find((a) => !a.geloescht_am) ?? null;
  const befund = e.pruefbefunde.find((p) => !p.geloescht_am) ?? null;
  const angenommen = e.angebote.find((a) => a.status === 'angenommen') ?? null;

  async function legeAuftragAn(auftragsart: string): Promise<void> {
    const vorlage = e.vorlagen.find((v) => v.auftragsart === auftragsart);
    const neu: Auftragszeile = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: e.projektId,
      angebot_id: angenommen?.id ?? null,
      status: 'offen',
      auftragsart,
      montage_von: null,
      montage_bis: null,
      monteur: null,
      checkliste: (vorlage?.punkte ?? []).map((text) => ({ text, erledigt: false })),
      unterschrift_schluessel: null,
      unterschrift_name: null,
      abgenommen_am: null,
    };
    await speichere('auftraege', neu, {
      projekt_id: neu.projekt_id,
      angebot_id: neu.angebot_id,
      status: neu.status,
      auftragsart: neu.auftragsart,
      checkliste: neu.checkliste,
    });
    await hakeAbFallsOffen(e.projektId, 'auftrag');
  }

  async function aendere(felder: Partial<Auftragszeile>): Promise<void> {
    if (!auftrag) return;
    await speichere('auftraege', auftrag, felder as Record<string, unknown>);
  }

  async function hakeAb(index: number): Promise<void> {
    if (!auftrag) return;
    // Auf der Liste in der Datenbank: Zwei Punkte schnell hintereinander
    // abgehakt — vorher war danach nur der zweite abgehakt.
    await speichere('auftraege', auftrag, (aktuell: Auftragszeile) => ({
      checkliste: aktuell.checkliste.map((punkt, i) =>
        i === index
          ? {
              ...punkt,
              erledigt: !punkt.erledigt,
              ...(punkt.erledigt ? {} : { erledigt_am: new Date().toISOString() }),
            }
          : punkt,
      ),
    }));
  }

  const offeneStrings = e.strings.filter((s) => !s.geloescht_am);
  const erledigt = auftrag?.checkliste.filter((p) => p.erledigt).length ?? 0;

  return (
    <div>
      <h2>Auftrag</h2>

      {!auftrag ? (
        <>
          {!angenommen && (
            <div className="hinweis">
              Noch kein angenommenes Angebot. Ein Auftrag lässt sich trotzdem anlegen — dann ohne
              Bezug.
            </div>
          )}
          <div className="knopfreihe">
            {e.vorlagen.map((vorlage) => (
              <button
                key={vorlage.id}
                className={vorlage.auftragsart === 'pv' ? 'wichtig' : undefined}
                onClick={() => void legeAuftragAn(vorlage.auftragsart)}
              >
                {vorlage.bezeichnung}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <dl className="zahlenblock">
            <div>
              <dt>Status</dt>
              <dd>{auftrag.status}</dd>
            </div>
            <div>
              <dt>Checkliste</dt>
              <dd>
                {erledigt} von {auftrag.checkliste.length}
              </dd>
            </div>
          </dl>

          <div className="knopfreihe">
            {(['offen', 'kommissioniert', 'montage', 'abgenommen'] as const).map((status) => (
              <button
                key={status}
                aria-pressed={auftrag.status === status}
                onClick={() => void aendere({ status })}
              >
                {status}
              </button>
            ))}
          </div>

          <div className="feld" style={{ marginTop: '0.8rem' }}>
            <label htmlFor="monteur">Monteur</label>
            <input
              id="monteur"
              defaultValue={auftrag.monteur ?? ''}
              onBlur={(ereignis) => void aendere({ monteur: ereignis.target.value || null })}
            />
          </div>
          <div className="knopfreihe">
            <label className="klein">
              Montage von
              <input
                type="date"
                key={`von-${tagVon(auftrag.montage_von) ?? ''}`}
                defaultValue={tagVon(auftrag.montage_von) ?? ''}
                onBlur={(ereignis) => {
                  const wert = ereignis.target.value || null;
                  if (wert !== tagVon(auftrag.montage_von)) void aendere({ montage_von: wert });
                }}
              />
            </label>
            <label className="klein">
              bis
              <input
                type="date"
                key={`bis-${tagVon(auftrag.montage_bis) ?? ''}`}
                defaultValue={tagVon(auftrag.montage_bis) ?? ''}
                onBlur={(ereignis) => {
                  const wert = ereignis.target.value || null;
                  if (wert !== tagVon(auftrag.montage_bis)) void aendere({ montage_bis: wert });
                }}
              />
            </label>
          </div>

          {/* --- Monteursicht (PV-M01, PV-M02) --------------------------- */}
          <h2 style={{ marginTop: '1.5rem' }}>Für den Monteur</h2>
          <div className="knopfreihe">
            <button onClick={e.onStringplan} disabled={offeneStrings.length === 0}>
              Stringplan
            </button>
            <button onClick={e.onDokumentation}>Dokumentationspaket</button>
            <button onClick={e.onAnlagenblatt}>Anlagendokumentation</button>
          </div>
          <details style={{ fontSize: '0.82rem', margin: '0.6rem 0' }}>
            <summary>
              Materialliste zum Kommissionieren ({e.anlage.material.detailliert.length} Positionen)
            </summary>
            <ul style={{ marginTop: '0.4rem' }}>
              {e.anlage.material.detailliert.map((position, index) => (
                <li key={index}>
                  {position.menge} {position.einheit} — {position.bezeichnung}
                </li>
              ))}
            </ul>
          </details>

          {/* --- Checkliste (PV-M08) ------------------------------------- */}
          <h2 style={{ marginTop: '1.5rem' }}>Checkliste</h2>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {auftrag.checkliste.map((punkt, index) => (
              <li key={index} style={{ borderTop: '1px solid var(--linie)', padding: '0.4rem 0' }}>
                <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={punkt.erledigt}
                    onChange={() => void hakeAb(index)}
                    style={{ width: 'auto', minHeight: 0, marginTop: '0.2rem' }}
                  />
                  <span
                    style={{
                      fontSize: '0.88rem',
                      textDecoration: punkt.erledigt ? 'line-through' : undefined,
                      color: punkt.erledigt ? 'var(--grau)' : undefined,
                    }}
                  >
                    {mitUmlauten(punkt.text)}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {/* --- Unterschrift (PV-M05) ----------------------------------- */}
          <h2 style={{ marginTop: '1.5rem' }}>Abnahme</h2>
          <Unterschrift
            vorhanden={auftrag.unterschrift_schluessel}
            name={auftrag.unterschrift_name}
            onGespeichert={(name) =>
              void aendere({
                unterschrift_name: name,
                unterschrift_schluessel: `unterschrift-${auftrag.id}`,
                abgenommen_am: new Date().toISOString(),
                status: 'abgenommen',
              }).then(() => hakeAbFallsOffen(e.projektId, 'montage'))
            }
            projektId={e.projektId}
          />
        </>
      )}

      {/* --- Netzanschluss (Grundlage der Fertigstellungsanzeige) --------- */}
      <fieldset className="nurlesen" disabled={!!e.nurLesen}>
      <h2 style={{ marginTop: '1.5rem' }}>Netzanschluss</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--grau)', margin: '0 0 0.6rem' }}>
        Das, was das Netzpartner-Portal zusätzlich zur Planung verlangt. Die Meldung selbst bringt
        das Elektrounternehmen dort ein — das Programm bereitet sie vor.
      </p>
      <div className="knopfreihe">
        {(['ueberschuss', 'voll'] as const).map((art) => (
          <button
            key={art}
            aria-pressed={e.projekt.einspeiseart === art}
            onClick={() => void speichere('projekte', e.projekt, { einspeiseart: art })}
          >
            {art === 'ueberschuss' ? 'Überschusseinspeisung' : 'Volleinspeisung'}
          </button>
        ))}
        {([1, 3] as const).map((phasen) => (
          <button
            key={phasen}
            aria-pressed={e.projekt.phasen === phasen}
            onClick={() => void speichere('projekte', e.projekt, { phasen })}
          >
            {phasen}-phasig
          </button>
        ))}
      </div>
      <div className="knopfreihe">
        <label className="klein">
          Einspeisepunkt
          <input
            defaultValue={e.projekt.einspeisepunkt ?? ''}
            onBlur={(ereignis) =>
              void speichere('projekte', e.projekt, {
                einspeisepunkt: ereignis.target.value || null,
              })
            }
          />
        </label>
        <label className="klein">
          Art des Zählers
          <input
            defaultValue={e.projekt.zaehlerart ?? ''}
            onBlur={(ereignis) =>
              void speichere('projekte', e.projekt, { zaehlerart: ereignis.target.value || null })
            }
          />
        </label>
        <label className="klein">
          Inbetriebnahme
          <input
            type="date"
            key={`ib-${tagVon(e.projekt.inbetriebnahme) ?? ''}`}
            defaultValue={tagVon(e.projekt.inbetriebnahme) ?? ''}
            onBlur={(ereignis) => {
              const wert = ereignis.target.value || null;
              if (wert !== tagVon(e.projekt.inbetriebnahme)) {
                void speichere('projekte', e.projekt, { inbetriebnahme: wert });
              }
            }}
          />
        </label>
      </div>

      </fieldset>

      {/* --- Pruefbefund (PV-M04) ---------------------------------------- */}
      <Pruefbefundpanel
        projektId={e.projektId}
        auftragId={auftrag?.id ?? null}
        befund={befund}
        vorlage={e.pruefvorlage}
        anlage={e.anlage}
        strings={e.strings}
      />
    </div>
  );
}

/**
 * Unterschrift am Tablet (PV-M05).
 *
 * Ein Zeichenfeld, mehr braucht es nicht. Das Bild wird als Dokument des
 * Projekts abgelegt und geht mit dem nächsten Abgleich hoch. Vorher lag es
 * unter einem Schlüssel in der Fotoablage, den nichts hochlud — die
 * Unterschrift blieb auf dem Gerät, auf dem sie geleistet wurde.
 */
function Unterschrift({
  vorhanden,
  name,
  projektId,
  onGespeichert,
}: {
  vorhanden: string | null;
  name: string | null;
  projektId: string;
  onGespeichert: (name: string) => void;
}): React.JSX.Element {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const zeichnet = useRef(false);
  const [unterzeichner, setUnterzeichner] = useState(name ?? '');

  // Die Leinwand ist intern 520 Punkte breit, am Schirm aber so breit wie das
  // Panel. Ohne Umrechnung saß der Strich neben dem Finger.
  function punktIn(ereignis: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const bild = leinwand.current!;
    const kasten = bild.getBoundingClientRect();
    return {
      x: ((ereignis.clientX - kasten.left) * bild.width) / kasten.width,
      y: ((ereignis.clientY - kasten.top) * bild.height) / kasten.height,
    };
  }

  function beginne(ereignis: React.PointerEvent<HTMLCanvasElement>): void {
    zeichnet.current = true;
    ereignis.currentTarget.setPointerCapture(ereignis.pointerId);
    const stift = leinwand.current?.getContext('2d');
    if (!stift) return;
    const p = punktIn(ereignis);
    stift.beginPath();
    stift.moveTo(p.x, p.y);
  }

  function ziehe(ereignis: React.PointerEvent<HTMLCanvasElement>): void {
    if (!zeichnet.current) return;
    const stift = leinwand.current?.getContext('2d');
    if (!stift) return;
    const p = punktIn(ereignis);
    stift.lineWidth = 2;
    stift.lineCap = 'round';
    // Eine CSS-Variable kennt die Leinwand nicht.
    stift.strokeStyle = '#111';
    stift.lineTo(p.x, p.y);
    stift.stroke();
  }

  async function sichere(): Promise<void> {
    const bild = leinwand.current;
    if (!bild || !unterzeichner.trim()) return;
    const blob = await new Promise<Blob | null>((fertig) => bild.toBlob(fertig, 'image/png'));
    if (!blob) return;
    const datei = new File([blob], `Unterschrift Abnahme ${unterzeichner.trim()}.png`, { type: 'image/png' });
    await legeDokumentAb(projektId, 'sonstiges', datei);
    onGespeichert(unterzeichner.trim());
  }

  if (vorhanden) {
    return (
      <p style={{ fontSize: '0.88rem' }}>
        Abgenommen von <strong>{name}</strong>. Die Unterschrift liegt unter den Dokumenten im Ablauf.
      </p>
    );
  }

  return (
    <div>
      <canvas
        ref={leinwand}
        width={520}
        height={140}
        onPointerDown={beginne}
        onPointerMove={ziehe}
        onPointerUp={() => (zeichnet.current = false)}
        onPointerLeave={() => (zeichnet.current = false)}
        style={{
          width: '100%',
          height: 'auto',
          border: '1px solid var(--linie)',
          background: 'var(--weiss)',
          touchAction: 'none',
        }}
      />
      <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
        <input
          placeholder="Name des Kunden"
          value={unterzeichner}
          onChange={(ereignis) => setUnterzeichner(ereignis.target.value)}
          style={{ width: '12rem' }}
        />
        <button
          onClick={() => {
            const stift = leinwand.current?.getContext('2d');
            if (stift && leinwand.current) {
              stift.clearRect(0, 0, leinwand.current.width, leinwand.current.height);
            }
          }}
        >
          Löschen
        </button>
        <button className="wichtig" onClick={() => void sichere()} disabled={!unterzeichner.trim()}>
          Abnahme bestätigen
        </button>
      </div>
    </div>
  );
}
